import React, { useEffect, useState, useCallback } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  InfoCard,
  StatusOK,
  StatusError,
  StatusPending,
  StatusRunning,
} from '@backstage/core-components';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  Link,
  Chip,
  IconButton,
  Divider,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import RefreshIcon from '@material-ui/icons/Refresh';
import SyncIcon from '@material-ui/icons/Sync';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import AccountTreeIcon from '@material-ui/icons/AccountTree';

export const isArgoCDPromotionAvailable = (entity: any) =>
  Boolean(entity?.metadata?.annotations?.['argocd/app-name']);

type EnvironmentInfo = {
  env: string;
  appName: string;
  syncStatus: string;
  healthStatus: string;
  namespace: string;
  sourcePath: string;
  lastSyncTime: string;
};

type AppInfo = {
  name: string;
  fileName: string;
  syncStatus: string;
  healthStatus: string;
  namespace: string;
  lastSyncTime: string;
  deployed: boolean;
};

type ResourceNode = {
  group: string;
  kind: string;
  name: string;
  namespace: string;
  version: string;
  health?: { status: string; message?: string };
  uid: string;
  parentRefs?: { uid: string }[];
  createdAt?: string;
  resourceVersion?: string;
  info?: { name: string; value: string }[];
};

type ActionResult =
  | { ok: true; message: string; prUrl?: string }
  | { ok: false; message: string };

const useStyles = makeStyles(theme => ({
  chip: {
    marginLeft: theme.spacing(0.5),
  },
  actionCell: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  resultText: {
    fontSize: '0.78rem',
    marginTop: theme.spacing(0.5),
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: '0.78rem',
    marginTop: theme.spacing(0.5),
  },
}));

function SyncStatus({ status }: { status: string }) {
  switch (status) {
    case 'Synced':
      return <StatusOK>{status}</StatusOK>;
    case 'OutOfSync':
      return <StatusError>{status}</StatusError>;
    case 'Unknown':
      return <StatusPending>{status}</StatusPending>;
    default:
      return <StatusPending>{status}</StatusPending>;
  }
}

function HealthStatus({ status }: { status: string }) {
  switch (status) {
    case 'Healthy':
      return <StatusOK>{status}</StatusOK>;
    case 'Degraded':
      return <StatusError>{status}</StatusError>;
    case 'Progressing':
      return <StatusRunning>{status}</StatusRunning>;
    case 'Suspended':
      return <StatusPending>{status}</StatusPending>;
    case 'Missing':
      return <StatusError>{status}</StatusError>;
    default:
      return <StatusPending>{status}</StatusPending>;
  }
}

/* ── Resource Map Diagram ─────────────────────────────────────────── */
const NODE_W = 146;
const NODE_H = 56;
const H_GAP = 14;
const V_GAP = 52;
const PAD = 24;

function healthColor(status?: string) {
  switch (status) {
    case 'Healthy': return '#4caf50';
    case 'Progressing': return '#ff9800';
    case 'Degraded': case 'Missing': return '#f44336';
    default: return '#9e9e9e';
  }
}

function ResourceMapDiagram({
  nodes,
  onClickNode,
}: {
  nodes: ResourceNode[];
  onClickNode: (n: ResourceNode) => void;
}) {
  if (!nodes.length) return null;

  // Build uid map and adjacency
  const byUid = new Map<string, ResourceNode>();
  nodes.forEach(n => byUid.set(n.uid, n));

  const children = new Map<string, string[]>();
  const roots: string[] = [];
  nodes.forEach(n => {
    if (!n.parentRefs || n.parentRefs.length === 0) {
      roots.push(n.uid);
    } else {
      n.parentRefs.forEach(p => {
        const list = children.get(p.uid) ?? [];
        list.push(n.uid);
        children.set(p.uid, list);
      });
    }
  });

  // BFS to assign rows
  const rows: string[][] = [];
  let frontier = roots;
  while (frontier.length) {
    rows.push(frontier);
    const next: string[] = [];
    frontier.forEach(uid => {
      (children.get(uid) ?? []).forEach(c => next.push(c));
    });
    frontier = next;
  }

  // Compute positions
  const pos = new Map<string, { x: number; y: number }>();
  rows.forEach((row, ri) => {
    const totalW = row.length * NODE_W + (row.length - 1) * H_GAP;
    const startX = PAD + (rows.reduce((mx, r) => Math.max(mx, r.length), 0) * NODE_W + (rows.reduce((mx, r) => Math.max(mx, r.length), 0) - 1) * H_GAP - totalW) / 2;
    row.forEach((uid, ci) => {
      pos.set(uid, {
        x: startX + ci * (NODE_W + H_GAP),
        y: PAD + ri * (NODE_H + V_GAP),
      });
    });
  });

  const maxCols = rows.reduce((mx, r) => Math.max(mx, r.length), 0);
  const svgW = PAD * 2 + maxCols * NODE_W + (maxCols - 1) * H_GAP;
  const svgH = PAD * 2 + rows.length * NODE_H + (rows.length - 1) * V_GAP;

  // Edges
  const edges: { x1: number; y1: number; x2: number; y2: number }[] = [];
  nodes.forEach(n => {
    (n.parentRefs ?? []).forEach(p => {
      const pp = pos.get(p.uid);
      const cp = pos.get(n.uid);
      if (pp && cp) {
        edges.push({
          x1: pp.x + NODE_W / 2,
          y1: pp.y + NODE_H,
          x2: cp.x + NODE_W / 2,
          y2: cp.y,
        });
      }
    });
  });

  return (
    <svg width={svgW} height={svgH} style={{ display: 'block', margin: '0 auto' }}>
      {edges.map((e, i) => {
        const midY = (e.y1 + e.y2) / 2;
        return (
          <path
            key={i}
            d={`M${e.x1},${e.y1} C${e.x1},${midY} ${e.x2},${midY} ${e.x2},${e.y2}`}
            fill="none"
            stroke="#666"
            strokeWidth={1.5}
            opacity={0.5}
          />
        );
      })}
      {nodes.map(n => {
        const p = pos.get(n.uid);
        if (!p) return null;
        const hc = healthColor(n.health?.status);
        return (
          <g
            key={n.uid}
            style={{ cursor: 'pointer' }}
            onClick={() => onClickNode(n)}
          >
            <rect
              x={p.x}
              y={p.y}
              width={NODE_W}
              height={NODE_H}
              rx={6}
              fill="#1e1e1e"
              stroke="#444"
              strokeWidth={1}
            />
            <circle cx={p.x + 12} cy={p.y + NODE_H / 2} r={5} fill={hc} />
            <text
              x={p.x + 22}
              y={p.y + 20}
              fill="#fff"
              fontSize={11}
              fontWeight={600}
            >
              {n.kind}
            </text>
            <text
              x={p.x + 22}
              y={p.y + 38}
              fill="#aaa"
              fontSize={10}
            >
              {n.name.length > 16 ? `${n.name.slice(0, 15)}…` : n.name}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export const ArgoCDPromotionContent = () => {
  const { entity } = useEntity();
  const discovery = useApi(discoveryApiRef);
  const { fetch: authedFetch } = useApi(fetchApiRef);
  const classes = useStyles();
  const theme = useTheme();

  const argoAppName =
    entity.metadata.annotations?.['argocd/app-name'] ?? '';

  // State
  const [envs, setEnvs] = useState<EnvironmentInfo[]>([]);
  const [repoSlug, setRepoSlug] = useState('');
  const [appPresence, setAppPresence] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  // Per-env app lists
  const [appsOpen, setAppsOpen] = useState<Record<string, boolean>>({});
  const [appsData, setAppsData] = useState<Record<string, AppInfo[]>>({});
  const [appsFetching, setAppsFetching] = useState<
    Record<string, boolean>
  >({});

  // Sync action
  const [syncLoading, setSyncLoading] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<ActionResult | null>(null);

  // Promote action
  const [promoteLoading, setPromoteLoading] = useState<string | null>(
    null,
  );
  const [promoteResult, setPromoteResult] = useState<ActionResult | null>(
    null,
  );

  // Promote dialog
  const [promoteDialog, setPromoteDialog] = useState<{
    fromEnv: EnvironmentInfo;
    toEnv: EnvironmentInfo;
    scope: 'single' | 'all';
    appFileName?: string;
  } | null>(null);

  // Resource tree
  const [resourceTreeOpen, setResourceTreeOpen] = useState<Record<string, boolean>>({});
  const [resourceTreeData, setResourceTreeData] = useState<Record<string, ResourceNode[]>>({});
  const [resourceTreeFetching, setResourceTreeFetching] = useState<Record<string, boolean>>({});
  const [detailNode, setDetailNode] = useState<ResourceNode | null>(null);

  const getBaseUrl = useCallback(
    () => discovery.getBaseUrl('app'),
    [discovery],
  );

  // Toggle resource tree for an app
  const toggleResourceTree = useCallback(
    async (appName: string) => {
      if (resourceTreeData[appName] !== undefined) {
        setResourceTreeOpen(prev => ({ ...prev, [appName]: !prev[appName] }));
        return;
      }
      setResourceTreeOpen(prev => ({ ...prev, [appName]: true }));
      setResourceTreeFetching(prev => ({ ...prev, [appName]: true }));
      try {
        const baseUrl = await getBaseUrl();
        const resp = await authedFetch(
          `${baseUrl}/api/argocd-promotion/resource-tree?appName=${encodeURIComponent(appName)}`,
        );
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        setResourceTreeData(prev => ({ ...prev, [appName]: data.nodes ?? [] }));
      } catch {
        setResourceTreeData(prev => ({ ...prev, [appName]: [] }));
      } finally {
        setResourceTreeFetching(prev => ({ ...prev, [appName]: false }));
      }
    },
    [resourceTreeData, getBaseUrl, authedFetch],
  );

  // Load environments
  const load = useCallback(async () => {
    if (!argoAppName) return;
    setLoading(true);
    setError(undefined);
    try {
      const baseUrl = await getBaseUrl();
      const resp = await authedFetch(
        `${baseUrl}/api/argocd-promotion/environments?argoAppName=${encodeURIComponent(argoAppName)}`,
      );
      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.error || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      setEnvs(data.environments ?? []);
      if (data.repoSlug) setRepoSlug(data.repoSlug);
      if (data.appPresence) setAppPresence(data.appPresence);
    } catch (e: any) {
      setError(e.message ?? String(e));
    } finally {
      setLoading(false);
    }
  }, [argoAppName, getBaseUrl, authedFetch]);

  useEffect(() => {
    load();
  }, [load]);

  // Toggle app list for an environment
  const toggleApps = useCallback(
    async (env: EnvironmentInfo) => {
      if (appsData[env.env] !== undefined) {
        setAppsOpen(prev => ({
          ...prev,
          [env.env]: !prev[env.env],
        }));
        return;
      }
      setAppsOpen(prev => ({ ...prev, [env.env]: true }));
      setAppsFetching(prev => ({ ...prev, [env.env]: true }));
      try {
        const baseUrl = await getBaseUrl();
        const resp = await authedFetch(
          `${baseUrl}/api/argocd-promotion/apps?repoSlug=${encodeURIComponent(repoSlug)}&env=${encodeURIComponent(env.env)}&argoAppName=${encodeURIComponent(argoAppName)}&namespace=${encodeURIComponent(env.namespace)}`,
        );
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        setAppsData(prev => ({
          ...prev,
          [env.env]: data.apps ?? [],
        }));
      } catch {
        setAppsData(prev => ({ ...prev, [env.env]: [] }));
      } finally {
        setAppsFetching(prev => ({ ...prev, [env.env]: false }));
      }
    },
    [appsData, getBaseUrl, authedFetch, repoSlug],
  );

  // Sync handler
  const handleSync = async (appName: string) => {
    setSyncLoading(appName);
    setSyncResult(null);
    try {
      const baseUrl = await getBaseUrl();
      const resp = await authedFetch(
        `${baseUrl}/api/argocd-promotion/sync`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ appName }),
        },
      );
      const data = await resp.json();
      setSyncResult(
        resp.ok
          ? { ok: true, message: data.message }
          : { ok: false, message: data.error || `HTTP ${resp.status}` },
      );
      if (resp.ok) setTimeout(() => load(), 3000);
    } catch (err: any) {
      setSyncResult({
        ok: false,
        message: err.message || 'Sync failed',
      });
    } finally {
      setSyncLoading(null);
    }
  };

  // Promote handler
  const handlePromote = async (
    fromEnv: EnvironmentInfo,
    toEnv: EnvironmentInfo,
    scope: 'single' | 'all',
    appFileName?: string,
  ) => {
    const key = `${fromEnv.env}->${toEnv.env}`;
    setPromoteLoading(key);
    setPromoteResult(null);
    setPromoteDialog(null);

    const [repoOwner, repoName] = repoSlug.split('/');
    const toNamespace = toEnv.namespace;

    try {
      const baseUrl = await getBaseUrl();

      if (scope === 'all') {
        const resp = await authedFetch(
          `${baseUrl}/api/argocd-promotion/promote-all`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              repoOwner,
              repoName,
              fromEnv: fromEnv.env,
              toEnv: toEnv.env,
              toNamespace,
            }),
          },
        );
        const data = await resp.json();
        setPromoteResult(
          resp.ok && data.success
            ? { ok: true, message: data.message, prUrl: data.prUrl }
            : {
                ok: false,
                message: data.error || data.message || `HTTP ${resp.status}`,
              },
        );
      } else if (appFileName) {
        const resp = await authedFetch(
          `${baseUrl}/api/argocd-promotion/promote`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              repoOwner,
              repoName,
              appFileName,
              fromEnv: fromEnv.env,
              toEnv: toEnv.env,
              toNamespace,
            }),
          },
        );
        const data = await resp.json();
        setPromoteResult(
          resp.ok
            ? { ok: true, message: data.message, prUrl: data.prUrl }
            : {
                ok: false,
                message: data.error || `HTTP ${resp.status}`,
              },
        );
      }

      if (promoteResult?.ok) {
        setTimeout(() => load(), 3000);
      }
    } catch (err: any) {
      setPromoteResult({
        ok: false,
        message: err.message || 'Promote failed',
      });
    } finally {
      setPromoteLoading(null);
    }
  };

  if (!argoAppName) {
    return (
      <InfoCard title="ArgoCD Environments">
        <Typography color="error">
          Missing annotation:{' '}
          <code>argocd/app-name</code> is required.
        </Typography>
      </InfoCard>
    );
  }

  return (
    <>
      <InfoCard
        title="ArgoCD Environments"
        subheader={
          <>
            Repository:{' '}
            <Link
              href={`https://gitea.kyndemo.live/${repoSlug}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {repoSlug}
            </Link>
          </>
        }
        action={
          <IconButton
            onClick={load}
            disabled={loading}
            aria-label="refresh"
          >
            <RefreshIcon />
          </IconButton>
        }
      >
        {loading && (
          <Box>
            {[1, 2, 3].map(n => (
              <Box
                key={n}
                style={{
                  height: 48,
                  marginBottom: 8,
                  borderRadius: 4,
                  backgroundColor: theme.palette.action.hover,
                }}
              />
            ))}
          </Box>
        )}
        {!loading && error && (
          <Typography color="error">
            Failed to load data: {error}
          </Typography>
        )}
        {!loading && !error && envs.length === 0 && (
          <Typography color="textSecondary">
            No environments found. Use the &quot;Create Promotion
            Environment&quot; template to add staging/production
            environments.
          </Typography>
        )}
        {!loading && !error && envs.length > 0 && (
          <Box>
            {/* Header row */}
            <Box
              display="flex"
              style={{
                padding: '6px 16px',
                borderBottom: `1px solid ${theme.palette.divider}`,
                opacity: 0.6,
              }}
            >
              <Box style={{ flex: 2 }}>
                <Typography variant="caption">ENVIRONMENT</Typography>
              </Box>
              <Box style={{ flex: 1.5 }}>
                <Typography variant="caption">NAMESPACE</Typography>
              </Box>
              <Box style={{ flex: 1.5 }}>
                <Typography variant="caption">SYNC</Typography>
              </Box>
              <Box style={{ flex: 1.5 }}>
                <Typography variant="caption">HEALTH</Typography>
              </Box>
              <Box style={{ flex: 2 }}>
                <Typography variant="caption">LAST SYNC</Typography>
              </Box>
              <Box style={{ flex: 2 }}>
                <Typography variant="caption">ACTIONS</Typography>
              </Box>
            </Box>
            {envs.map((env, idx) => {
              const isAppsOpen = Boolean(appsOpen[env.env]);
              const nextEnv = envs[idx + 1];
              const isProd =
                ['prod', 'production'].includes(
                  env.env.toLowerCase(),
                );
              const showPromote = !isProd && Boolean(nextEnv);

              return (
                <React.Fragment key={env.env}>
                  <Box
                    style={{
                      borderBottom: showPromote
                        ? 'none'
                        : `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    {/* Data row */}
                    <Box
                      display="flex"
                      alignItems="center"
                      style={{ padding: '10px 16px' }}
                    >
                      <Box style={{ flex: 2 }}>
                        <Link
                          href={`https://argocd.kyndemo.live/applications/${env.appName}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontWeight: 700 }}
                        >
                          {env.env}
                        </Link>
                        <Chip
                          size="small"
                          label={env.appName}
                          className={classes.chip}
                          variant="outlined"
                        />
                        {appPresence[env.env] !== undefined && (
                          <Chip
                            size="small"
                            label={appPresence[env.env] ? argoAppName : `${argoAppName} not deployed`}
                            className={classes.chip}
                            variant="outlined"
                            color={appPresence[env.env] ? 'primary' : 'default'}
                          />
                        )}
                      </Box>
                      <Box style={{ flex: 1.5 }}>
                        <Typography variant="body2">
                          {env.namespace || '—'}
                        </Typography>
                      </Box>
                      <Box style={{ flex: 1.5 }}>
                        <SyncStatus status={env.syncStatus} />
                      </Box>
                      <Box style={{ flex: 1.5 }}>
                        <HealthStatus status={env.healthStatus} />
                      </Box>
                      <Box style={{ flex: 2 }}>
                        {env.lastSyncTime ? (
                          <Typography
                            variant="caption"
                            color="textSecondary"
                          >
                            {new Date(
                              env.lastSyncTime,
                            ).toLocaleString()}
                          </Typography>
                        ) : (
                          <Typography
                            variant="body2"
                            color="textSecondary"
                          >
                            —
                          </Typography>
                        )}
                      </Box>
                      <Box
                        style={{ flex: 2 }}
                        className={classes.actionCell}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          startIcon={
                            syncLoading === env.appName ? (
                              <CircularProgress size={14} />
                            ) : (
                              <SyncIcon fontSize="small" />
                            )
                          }
                          onClick={() => handleSync(env.appName)}
                          disabled={Boolean(syncLoading)}
                        >
                          Sync
                        </Button>
                        <Button
                          size="small"
                          variant={isAppsOpen ? 'contained' : 'outlined'}
                          endIcon={
                            isAppsOpen ? (
                              <ExpandLessIcon fontSize="small" />
                            ) : (
                              <ExpandMoreIcon fontSize="small" />
                            )
                          }
                          onClick={() => toggleApps(env)}
                        >
                          Apps
                        </Button>
                      </Box>
                    </Box>

                    {/* Expanded app list */}
                    <Collapse in={isAppsOpen}>
                      <Box
                        style={{
                          padding: '8px 32px 16px',
                          backgroundColor:
                            theme.palette.background.default,
                          borderTop: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        {appsFetching[env.env] ? (
                          <Box
                            display="flex"
                            justifyContent="center"
                            py={2}
                          >
                            <CircularProgress size={24} />
                          </Box>
                        ) : (appsData[env.env] ?? []).length === 0 ? (
                          <Typography
                            variant="body2"
                            color="textSecondary"
                          >
                            No applications deployed in this
                            environment.
                          </Typography>
                        ) : (
                          <>
                            {/* App list header */}
                            <Box
                              display="flex"
                              style={{
                                padding: '4px 0',
                                opacity: 0.5,
                              }}
                            >
                              <Box style={{ flex: 2 }}>
                                <Typography variant="caption">
                                  APP
                                </Typography>
                              </Box>
                              <Box style={{ flex: 1.5 }}>
                                <Typography variant="caption">
                                  NAMESPACE
                                </Typography>
                              </Box>
                              <Box style={{ flex: 1 }}>
                                <Typography variant="caption">
                                  SYNC
                                </Typography>
                              </Box>
                              <Box style={{ flex: 1 }}>
                                <Typography variant="caption">
                                  HEALTH
                                </Typography>
                              </Box>
                              <Box style={{ flex: 1 }}>
                                <Typography variant="caption">
                                  STATUS
                                </Typography>
                              </Box>
                              <Box style={{ flex: 0.5 }}>
                                <Typography variant="caption">
                                  MAP
                                </Typography>
                              </Box>
                            </Box>
                            {(appsData[env.env] ?? []).map(app => (
                              <React.Fragment key={app.name}>
                              <Box
                                display="flex"
                                alignItems="center"
                                style={{
                                  padding: '6px 0',
                                  borderBottom: `1px solid ${theme.palette.divider}`,
                                }}
                              >
                                <Box style={{ flex: 2 }}>
                                  <Link
                                    href={`https://argocd.kyndemo.live/applications/${app.name}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {app.name}
                                  </Link>
                                </Box>
                                <Box style={{ flex: 1.5 }}>
                                  <Typography variant="body2">
                                    {app.namespace}
                                  </Typography>
                                </Box>
                                <Box style={{ flex: 1 }}>
                                  <SyncStatus
                                    status={app.syncStatus}
                                  />
                                </Box>
                                <Box style={{ flex: 1 }}>
                                  <HealthStatus
                                    status={app.healthStatus}
                                  />
                                </Box>
                                <Box style={{ flex: 1 }}>
                                  <Chip
                                    size="small"
                                    label={app.deployed ? 'Live' : 'Pending'}
                                    color={app.deployed ? 'primary' : 'default'}
                                    variant="outlined"
                                  />
                                </Box>
                                <Box style={{ flex: 0.5 }}>
                                  {app.deployed && (
                                    <IconButton
                                      size="small"
                                      onClick={() => toggleResourceTree(app.name)}
                                      title="Resource tree"
                                    >
                                      <AccountTreeIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </Box>
                              </Box>
                              {app.deployed && (
                                <Collapse in={Boolean(resourceTreeOpen[app.name])}>
                                  <Box
                                    style={{
                                      padding: '12px 0',
                                      overflowX: 'auto',
                                    }}
                                  >
                                    {resourceTreeFetching[app.name] ? (
                                      <Box display="flex" justifyContent="center" py={2}>
                                        <CircularProgress size={24} />
                                      </Box>
                                    ) : (resourceTreeData[app.name] ?? []).length === 0 ? (
                                      <Typography variant="body2" color="textSecondary">
                                        No resources found.
                                      </Typography>
                                    ) : (
                                      <ResourceMapDiagram
                                        nodes={resourceTreeData[app.name]}
                                        onClickNode={setDetailNode}
                                      />
                                    )}
                                  </Box>
                                </Collapse>
                              )}
                              </React.Fragment>
                            ))}
                          </>
                        )}
                      </Box>
                    </Collapse>
                  </Box>

                  {/* Promote strip between environments */}
                  {showPromote && nextEnv && (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      py={0.75}
                      style={{
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        backgroundColor:
                          theme.palette.background.default,
                        gap: 8,
                      }}
                    >
                      <Typography
                        variant="caption"
                        style={{ opacity: 0.5, marginRight: 4 }}
                      >
                        {env.env} → {nextEnv.env}:
                      </Typography>
                      {appPresence[env.env] && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          startIcon={
                            promoteLoading ===
                            `${env.env}->${nextEnv.env}` ? (
                              <CircularProgress size={12} />
                            ) : (
                              <ArrowForwardIcon
                                style={{ fontSize: 14 }}
                              />
                            )
                          }
                          onClick={() =>
                            setPromoteDialog({
                              fromEnv: env,
                              toEnv: nextEnv,
                              scope: 'single',
                              appFileName: `${argoAppName}.yaml`,
                            })
                          }
                          disabled={Boolean(promoteLoading)}
                          style={{
                            textTransform: 'none',
                            fontSize: '0.72rem',
                            padding: '2px 8px',
                          }}
                        >
                          Promote {argoAppName}
                        </Button>
                      )}
                      {!appPresence[env.env] && (
                        <Typography
                          variant="caption"
                          color="textSecondary"
                        >
                          Not deployed in {env.env}
                        </Typography>
                      )}
                    </Box>
                  )}
                </React.Fragment>
              );
            })}
          </Box>
        )}

        {/* Inline result feedback */}
        {(syncResult || promoteResult) && (
          <>
            <Divider style={{ margin: '12px 0' }} />
            {syncResult && (
              <Typography
                className={
                  syncResult.ok
                    ? classes.resultText
                    : classes.errorText
                }
              >
                Sync: {syncResult.message}
              </Typography>
            )}
            {promoteResult && (
              <Typography
                className={
                  promoteResult.ok
                    ? classes.resultText
                    : classes.errorText
                }
              >
                Promote: {promoteResult.message}
                {promoteResult.ok && promoteResult.prUrl && (
                  <>
                    {' — '}
                    <Link
                      href={promoteResult.prUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View PR
                    </Link>
                  </>
                )}
              </Typography>
            )}
          </>
        )}
      </InfoCard>

      {/* Promote confirmation dialog */}
      <Dialog
        open={Boolean(promoteDialog)}
        onClose={() =>
          !promoteLoading && setPromoteDialog(null)
        }
      >
        <DialogTitle>
          Promote {promoteDialog?.fromEnv.env} →{' '}
          {promoteDialog?.toEnv.env}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will raise a pull request to promote{' '}
            <strong>{promoteDialog?.appFileName ?? argoAppName}</strong>{' '}
            from{' '}
            <strong>
              apps/overlays/{promoteDialog?.fromEnv.env}/
            </strong>{' '}
            to{' '}
            <strong>
              apps/overlays/{promoteDialog?.toEnv.env}/
            </strong>{' '}
            in the GitOps repository. The PR must be reviewed and
            approved before the promotion is applied. Once merged,
            ArgoCD will automatically sync and deploy in the{' '}
            <strong>{promoteDialog?.toEnv.namespace}</strong>{' '}
            namespace.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPromoteDialog(null)}
            disabled={Boolean(promoteLoading)}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              if (promoteDialog) {
                handlePromote(
                  promoteDialog.fromEnv,
                  promoteDialog.toEnv,
                  promoteDialog.scope,
                  promoteDialog.appFileName,
                );
              }
            }}
            disabled={Boolean(promoteLoading)}
            startIcon={
              promoteLoading ? (
                <CircularProgress size={16} />
              ) : (
                <ArrowForwardIcon />
              )
            }
          >
            {promoteLoading ? 'Raising PR…' : 'Raise PR'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resource detail dialog */}
      <Dialog
        open={Boolean(detailNode)}
        onClose={() => setDetailNode(null)}
        maxWidth="sm"
        fullWidth
      >
        {detailNode && (
          <>
            <DialogTitle>
              {detailNode.kind}: {detailNode.name}
            </DialogTitle>
            <DialogContent>
              <Box display="flex" flexWrap="wrap" style={{ gap: 16 }}>
                <Box style={{ minWidth: 120 }}>
                  <Typography variant="caption" color="textSecondary">Kind</Typography>
                  <Typography variant="body2">{detailNode.kind}</Typography>
                </Box>
                {detailNode.group && (
                  <Box style={{ minWidth: 120 }}>
                    <Typography variant="caption" color="textSecondary">Group</Typography>
                    <Typography variant="body2">{detailNode.group}</Typography>
                  </Box>
                )}
                <Box style={{ minWidth: 120 }}>
                  <Typography variant="caption" color="textSecondary">Namespace</Typography>
                  <Typography variant="body2">{detailNode.namespace || '—'}</Typography>
                </Box>
                <Box style={{ minWidth: 120 }}>
                  <Typography variant="caption" color="textSecondary">Health</Typography>
                  <Chip
                    size="small"
                    label={detailNode.health?.status ?? 'Unknown'}
                    style={{
                      backgroundColor: healthColor(detailNode.health?.status),
                      color: '#fff',
                      fontWeight: 600,
                    }}
                  />
                </Box>
                {detailNode.version && (
                  <Box style={{ minWidth: 120 }}>
                    <Typography variant="caption" color="textSecondary">Version</Typography>
                    <Typography variant="body2">{detailNode.version}</Typography>
                  </Box>
                )}
                {detailNode.createdAt && (
                  <Box style={{ minWidth: 180 }}>
                    <Typography variant="caption" color="textSecondary">Created</Typography>
                    <Typography variant="body2">{new Date(detailNode.createdAt).toLocaleString()}</Typography>
                  </Box>
                )}
                {detailNode.resourceVersion && (
                  <Box style={{ minWidth: 120 }}>
                    <Typography variant="caption" color="textSecondary">Resource Version</Typography>
                    <Typography variant="body2">{detailNode.resourceVersion}</Typography>
                  </Box>
                )}
              </Box>
              {detailNode.health?.message && (
                <Box mt={2}>
                  <Typography variant="caption" color="textSecondary">Health Message</Typography>
                  <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                    {detailNode.health.message}
                  </Typography>
                </Box>
              )}
              {detailNode.info && detailNode.info.length > 0 && (
                <Box mt={2}>
                  <Typography variant="caption" color="textSecondary">Info</Typography>
                  {detailNode.info.map((item, i) => (
                    <Typography key={i} variant="body2">
                      <strong>{item.name}:</strong> {item.value}
                    </Typography>
                  ))}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailNode(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};
